import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp, RootStackParamList } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import {
  BurialPlotData,
  burialPlotService,
} from '../../../../services/burialPlotInfo';
import { tasksService } from '../../../../services/tasks';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('BurialPlotData');

type RouteParams = {
  burialPlotId?: string;
  targetUserId?: string;
  viewOnly?: boolean;
};

type BurialPlotDataScreenRouteProp = RouteProp<RootStackParamList, 'BurialPlotData'>;

type InfoItemProps = {
  label: string;
  value: string | boolean | undefined;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => {
  let displayValue = '';

  if (typeof value === 'boolean') {
    displayValue = value ? 'Yes' : 'No';
  } else if (typeof value === 'string') {
    displayValue = value || 'Not specified';
  } else {
    displayValue = 'Not specified';
  }

  return (
    <View style={styles.infoItem}>
      <Text style={styles.label}>{label}</Text>
      <Text style={styles.value}>{displayValue}</Text>
      <View style={styles.separator} />
    </View>
  );
};

export const BurialPlotDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<BurialPlotDataScreenRouteProp>();
  const [burialPlotInfo, setBurialPlotInfo] = useState<BurialPlotData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const burialPlotId = route.params?.burialPlotId;
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();

  useEffect(() => {
    loadBurialPlotInfo();
  }, []);

  const loadBurialPlotInfo = async () => {
    try {
      let info = null;
      if (burialPlotId) {
        info = await burialPlotService.getBurialPlotById(burialPlotId);
      } else {
        // Load the first/latest entry if no specific ID provided
        const allInfo = await burialPlotService.getAllBurialPlots();
        if (allInfo.length > 0) {
          info = allInfo[0];
        }
      }

      if (info) {
        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          try {
            logger.debug('[Portal Debug] Attempting to load portal credentials for burial plot ID:', info.id);
            const portalData = await vaultService.getPortalCredentials(info.id, 'burial-plot');
            logger.debug('[Portal Debug] Portal data retrieved for burial plot:', info.id, portalData);
            if (portalData) {
              info.username = portalData.username;
              info.password = portalData.password;
              logger.debug('[Portal Debug] Assigned credentials to burial plot data');
            } else {
              logger.debug('[Portal Debug] No portal data returned for burial plot:', info.id);
            }
          } catch (vaultError) {
            logger.debug('[Portal Debug] Error loading portal data for burial plot:', info.id, vaultError);
          }
        }

        setBurialPlotInfo(info);
      }
    } catch (error) {
      logger.error('Error loading burial plot info:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    if (burialPlotInfo) {
      navigation.navigate('BurialPlotInput', {
        burialPlotId: burialPlotInfo.id,
        isEdit: true
      });
    } else {
      navigation.navigate('BurialPlotInput');
    }
  };

  const handleDelete = () => {
    if (burialPlotInfo) {
      Alert.alert(
        'Delete Burial Plot Information',
        'Are you sure you want to delete this burial plot information? This action cannot be undone.',
        [
          {
            text: 'Cancel',
            style: 'cancel',
          },
          {
            text: 'Delete',
            style: 'destructive',
            onPress: async () => {
              try {
                // Delete the record
                await burialPlotService.deleteBurialPlot(burialPlotInfo.id!);

                // CRITICAL: Check if this was the last entry and reset task if needed
                const remainingData = await burialPlotService.getAllBurialPlots();
                if (!remainingData || remainingData.length === 0) {
                  // Reset task completion status if no data remains
                  await tasksService.resetTask('burial_plot');
                }

                // Navigate back to checklist
                navigation.navigate('FinalWishesChecklist');
              } catch (error) {
                logger.error('Error deleting burial plot info:', error);
                Alert.alert('Error', 'Failed to delete burial plot information');
              }
            },
          },
        ]
      );
    }
  };

  const handleBack = () => {
    navigation.navigate('FinalWishesChecklist');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>
        <Text style={styles.title}>Burial Plot Information Details</Text>
        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <Text style={styles.loadingText}>Loading...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Burial Plot Information Details</Text>
      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {viewOnly && (
            <View style={styles.viewOnlyBanner}>
              <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
            </View>
          )}
          {burialPlotInfo ? (
            <>
              {burialPlotInfo.cemeteryName && (
                <InfoItem label="Cemetery Name" value={burialPlotInfo.cemeteryName} />
              )}
              {burialPlotInfo.cemeteryAddress && (
                <InfoItem label="Cemetery Address" value={burialPlotInfo.cemeteryAddress} />
              )}
              {burialPlotInfo.cemeteryPhone && (
                <InfoItem label="Cemetery Phone" value={burialPlotInfo.cemeteryPhone} />
              )}
              {burialPlotInfo.cemeteryEmail && (
                <InfoItem label="Cemetery Email" value={burialPlotInfo.cemeteryEmail} />
              )}
              {burialPlotInfo.cemeteryWebsite && (
                <InfoItem label="Cemetery Website" value={burialPlotInfo.cemeteryWebsite} />
              )}
              {burialPlotInfo.lotNumber && (
                <InfoItem label="Lot Number" value={burialPlotInfo.lotNumber} />
              )}
              {burialPlotInfo.deedLocation && (
                <InfoItem label="Deed Location" value={burialPlotInfo.deedLocation} />
              )}
              {burialPlotInfo.purchaseDate && (
                <InfoItem label="Purchase Date" value={burialPlotInfo.purchaseDate} />
              )}
              {burialPlotInfo.purchasePrice && (
                <InfoItem label="Purchase Price" value={`$${burialPlotInfo.purchasePrice}`} />
              )}
              {burialPlotInfo.notes && (
                <InfoItem label="Notes" value={burialPlotInfo.notes} />
              )}

              {/* Vault Credentials Section */}
              {(burialPlotInfo.username || burialPlotInfo.password) && (
                <>
                  <View style={styles.vaultSection}>
                    <Text style={styles.vaultSectionTitle}>🔒 Cemetery Portal Credentials</Text>
                    <Text style={styles.vaultSubtext}>Securely stored in your vault</Text>
                  </View>

                  {burialPlotInfo.username && (
                    <View style={styles.infoItem}>
                      <Text style={styles.label}>Cemetery Username</Text>
                      <View style={styles.credentialRow}>
                        <Text style={styles.value}>
                          {showUsername ? burialPlotInfo.username : '••••••••'}
                        </Text>
                        <TouchableOpacity
                          style={styles.eyeButton}
                          onPress={() => setShowUsername(!showUsername)}
                        >
                          <Image
                            source={showUsername ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                            style={styles.eyeIcon}
                          />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.separator} />
                    </View>
                  )}

                  {burialPlotInfo.password && (
                    <View style={styles.infoItem}>
                      <Text style={styles.label}>Cemetery Password</Text>
                      <View style={styles.credentialRow}>
                        <Text style={styles.value}>
                          {showPassword ? burialPlotInfo.password : '••••••••'}
                        </Text>
                        <TouchableOpacity
                          style={styles.eyeButton}
                          onPress={() => setShowPassword(!showPassword)}
                        >
                          <Image
                            source={showPassword ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                            style={styles.eyeIcon}
                          />
                        </TouchableOpacity>
                      </View>
                      <View style={styles.separator} />
                    </View>
                  )}
                </>
              )}

              <View style={styles.buttonContainer}>
                <TouchableOpacity
                  style={[styles.button, styles.backToListButton]}
                  onPress={handleBack}
                >
                  <Text style={styles.buttonText}>Back</Text>
                </TouchableOpacity>

                {!viewOnly && (
                  <>
                    <TouchableOpacity
                      style={[styles.button, styles.deleteButton]}
                      onPress={handleDelete}
                    >
                      <Text style={styles.buttonText}>Delete</Text>
                    </TouchableOpacity>

                    <TouchableOpacity
                      style={[styles.button, styles.editButton]}
                      onPress={handleEdit}
                    >
                      <Text style={styles.buttonText}>Edit</Text>
                    </TouchableOpacity>
                  </>
                )}
              </View>
            </>
          ) : (
            <View style={styles.noDataContainer}>
              <Text style={styles.noDataText}>No burial plot information found.</Text>
              {!viewOnly && (
                <TouchableOpacity
                  style={[styles.button, styles.editButton, { marginTop: 20 }]}
                  onPress={handleEdit}
                >
                  <Text style={styles.buttonText}>Add Burial Plot Information</Text>
                </TouchableOpacity>
              )}
            </View>
          )}
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  infoItem: {
    marginBottom: ms(20),
  },
  label: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
    marginBottom: ms(8),
  },
  value: {
    fontSize: ms(18, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(2),
    backgroundColor: '#29BFC0',
    marginTop: ms(8),
  },
  buttonContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: ms(20),
    paddingBottom: ms(130),
    gap: ms(10),
  },
  button: {
    flex: 1,
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  deleteButton: {
    backgroundColor: '#FF4444',
  },
  editButton: {
    backgroundColor: '#FF9F5A',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
  },
  noDataContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: ms(40),
  },
  noDataText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
  },
  vaultSection: {
    marginVertical: ms(20),
    paddingTop: ms(10),
    borderTopWidth: ms(2),
    borderTopColor: '#E5E7EB',
  },
  vaultSectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(5),
  },
  vaultSubtext: {
    fontSize: ms(14, 0.3),
    color: '#FF9F5A',
    marginBottom: ms(15),
  },
  credentialRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});

export default BurialPlotDataScreen;